import { createContext, useContext, useState, useCallback, useEffect, useMemo } from 'react';

/**
 * MapVRAMGuard — Sprint Layers (Feb 2026).
 *
 * Gestion centralisée de la VRAM pour la carte cartographique principale du
 * Hub.
 *
 * Problématique : WebGL n'a qu'un budget VRAM limité par contexte. Quand
 * plusieurs modules WebGL gourmands coexistent (Photorealistic 3D Tiles +
 * r3f WhiteboxViewer + Konva annotation), Chrome déclenche
 * `CONTEXT_LOST_WEBGL` et plante toute la session.
 *
 * Solution : un Context React global qui pilote l'état "réservé" de la VRAM.
 * Chaque module gourmand appelle `useReserveVRAM('module-name')` à son mount.
 * Le Hub3DMap observe le context et, si au moins une réservation est active
 * ET que l'utilisateur a sélectionné Photorealistic, force un downgrade
 * automatique vers Vector 3D (qui consomme ~10x moins de VRAM) pour libérer
 * de la place. À la dernière "release", la carte peut remonter au layer
 * choisi par l'utilisateur.
 *
 * L'API est volontairement minimale : 2 hooks (`useReserveVRAM`, `useVRAMState`)
 * et 1 Provider. Aucune logique de basculement de carte ici — c'est l'affaire
 * du Hub3DMap.
 */

const VRAMContext = createContext(null);

export const MapVRAMProvider = ({ children }) => {
  // Set of module IDs currently holding a VRAM reservation
  const [reservations, setReservations] = useState(() => new Set());

  const reserve = useCallback((moduleId) => {
    setReservations((prev) => {
      if (prev.has(moduleId)) return prev;
      const next = new Set(prev);
      next.add(moduleId);
      return next;
    });
  }, []);

  const release = useCallback((moduleId) => {
    setReservations((prev) => {
      if (!prev.has(moduleId)) return prev;
      const next = new Set(prev);
      next.delete(moduleId);
      return next;
    });
  }, []);

  const value = useMemo(() => ({
    reservations,
    reserve,
    release,
    hasReservations: reservations.size > 0,
    list: () => [...reservations],
  }), [reservations, reserve, release]);

  return <VRAMContext.Provider value={value}>{children}</VRAMContext.Provider>;
};

/**
 * Hook to *consume* the VRAM state (read-only). Used by Hub3DMap to react to
 * reservations and auto-downgrade layers.
 */
export const useVRAMState = () => {
  const ctx = useContext(VRAMContext);
  if (!ctx) {
    // Soft fallback: outside the provider, behave as if VRAM is free.
    return { hasReservations: false, reservations: new Set(), list: () => [] };
  }
  return ctx;
};

/**
 * Hook to *claim* a VRAM reservation for a module. Reserves on mount, releases
 * on unmount. Call once per heavy-WebGL component.
 *
 * Example:
 *   const WhiteboxViewer = () => {
 *     useReserveVRAM('whitebox-viewer');
 *     return <Canvas>...</Canvas>;
 *   };
 */
export const useReserveVRAM = (moduleId) => {
  const ctx = useContext(VRAMContext);
  useEffect(() => {
    if (!ctx || !moduleId) return undefined;
    ctx.reserve(moduleId);
    return () => ctx.release(moduleId);
  }, [ctx, moduleId]);
};
